import { unsafeCoerce } from 'fp-ts/lib/function';
import Cookie from 'js-cookie';
import { PreloadJob } from '../../../UnicronApp/preloader/PreloadJob';
import { Fetch } from '../../services/NetworkService';
import { env } from '../../util/env';
import { refreshLogin } from '../mom/refreshLogin';
import { API3Endpoint } from './Api3Endpoint';
import { Api3Error } from './Api3Error';
import { account } from './endpoints';
const PREFIX = '/api/3';

export const UNAUTHORIZED = 401;

export const RATE_LIMITED = 429;

export const TOKEN_INVALID = 409;

// wraps api3CallInternal with retry logic. We retry at most once, only in the case that we get a
// 401-unauthorized response and we refresh the mom credentials in-between.
export const api3Call = async <Return>(fetch: Fetch, endpoint: API3Endpoint<Return>) => {
  try {
    return await callInternal(fetch, endpoint);
  } catch (_error) {
    /** FIXME — when babel supports "catch(error: unknown)" syntax */
    const error: unknown = _error;

    if (error instanceof Api3Error && error.response.status === UNAUTHORIZED) {
      await refreshLogin(fetch);
      return callInternal(fetch, endpoint);
    }

    throw error;
  }
};

export const api3CallNoRetry = async <Return>(
  fetch: Fetch,
  endpoint: API3Endpoint<Return>,
): Promise<Return> => callInternal(fetch, endpoint);

const callInternal = async <Return>(
  fetch: Fetch,
  endpoint: API3Endpoint<Return>,
  withoutRecursion?: boolean,
): Promise<Return> => {
  const xsrf = Cookie.get('XSRF-TOKEN');

  if (xsrf) {
    endpoint.options.headers = { ...(endpoint.options.headers || {}), 'X-XSRF-TOKEN': xsrf };
  } else if (
    !withoutRecursion &&
    endpoint.options.method !== 'GET' &&
    endpoint.options.method !== 'OPTIONS'
  ) {
    return api3Call(fetch, account.getAccount()).then(() => callInternal(fetch, endpoint, true));
  }

  const response = await fetch(`${PREFIX}/${endpoint.url}`, endpoint.options);

  if (response.status !== 200) throw new Api3Error(response);
  return unsafeCoerce(response.json());
};

/* For now, we assume the endpoint will not change throughout the lifetime of the hook call*/

export const api3CallForPreload = async <Return>(
  job: PreloadJob,
  endpoint: API3Endpoint<Return>,
): Promise<Return> => {
  const { tracingHeaders } = job.services;
  const { fetch } = job.services.network;

  const cookies =
    job.server?.req.headers.cookie === undefined ? [] : { cookie: job.server?.req.headers.cookie };

  const response = await fetch(`${env.MEGATRON_HOST}${PREFIX}/${endpoint.url}`, {
    ...endpoint.options,
    headers: {
      ...endpoint.options.headers,
      ...tracingHeaders,
      ...cookies,
    },
  });

  if (response.status !== 200) throw new Api3Error(response);
  return unsafeCoerce(response.json());
};
